import { ChevronRight } from 'lucide-react';

// Adapted from Lovable mockup (github.com/jportell/pixel-perfect-83 src/components/PipelineRow.tsx)
// for the PageMind admin Pipeline Monitor dashboard. Same dot-grid bottom-up
// fill behaviour Jaume wanted ("buckets of products progressing slowly").

export type ItemStatus = 'pending' | 'progress' | 'error' | 'success';
export type FillLevel = 'empty' | 'half' | 'full';

export interface StageData {
  name: string;
  type: 'process' | 'wait' | 'hold' | 'done' | 'published';
  progress?: number; // 0-100 for process stages
  items: ItemStatus[];
}

export interface PipelineSiteRow {
  site: string;
  mode: string;
  count: number;
  stages: StageData[];
}

function Dot({ status, fill, color }: { status: ItemStatus; fill: FillLevel; color: string }) {
  const stroke =
    status === 'error'
      ? 'var(--item-error, #EF4444)'
      : status === 'success'
        ? 'var(--item-success, #10B981)'
        : color;
  const bg =
    fill === 'full'
      ? stroke
      : fill === 'half'
        ? `linear-gradient(to top, ${stroke} 50%, transparent 50%)`
        : 'transparent';
  return (
    <span
      className="h-2.5 w-2.5 rounded-full border transition-all duration-300"
      style={{ borderColor: stroke, background: bg }}
    />
  );
}

function ColumnDots({
  items,
  cols,
  progress,
  color,
}: {
  items: ItemStatus[];
  cols: number;
  progress: number;
  color: string;
}) {
  // Cap visible dots so a 1k-product batch doesn't render 1k DOM nodes
  const visibleItems = items.length > 60 ? items.slice(0, 60) : items;
  const rows = Math.ceil(visibleItems.length / cols);
  return (
    <div
      className="grid gap-1.5 justify-items-center"
      style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
    >
      {visibleItems.map((s, i) => {
        const rowFromTop = Math.floor(i / cols);
        const rowsFromBottom = rows - 1 - rowFromTop;
        const rowSize = 100 / rows;
        const fullThreshold = (rowsFromBottom + 1) * rowSize;
        const halfThreshold = (rowsFromBottom + 0.5) * rowSize;
        const fill: FillLevel =
          progress >= fullThreshold ? 'full' : progress >= halfThreshold ? 'half' : 'empty';
        return <Dot key={i} status={s} fill={fill} color={color} />;
      })}
    </div>
  );
}

function ProcessStage({ stage, color }: { stage: StageData; color: string }) {
  const pct = stage.progress ?? 0;
  return (
    <div className="flex flex-col gap-2 min-w-[120px]">
      <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-gray-500">
        <span>0%</span>
        <span>50%</span>
        <span>100%</span>
      </div>
      <div className="relative h-2 rounded-full bg-gray-200 overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 rounded-full transition-all duration-300"
          style={{ width: `${pct}%`, background: color }}
        />
        <div className="absolute inset-y-0 left-1/2 w-px bg-gray-300" />
      </div>
      <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-2.5">
        <ColumnDots items={stage.items} cols={3} progress={pct} color={color} />
      </div>
    </div>
  );
}

function WaitStage({ stage }: { stage: StageData }) {
  return (
    <div className="flex flex-col gap-2 min-w-[88px]">
      <div className="text-[10px] font-mono uppercase tracking-wider text-gray-400 leading-tight">
        Waiting
        <br />
        for cohort
      </div>
      <div className="rounded-md bg-gray-100 p-2.5">
        <ColumnDots items={stage.items} cols={3} progress={0} color="#6B7280" />
      </div>
    </div>
  );
}

function TerminalStage({ stage }: { stage: StageData }) {
  const tone =
    stage.type === 'published'
      ? 'border-green-300 bg-green-50'
      : 'border-blue-300 bg-blue-50';
  const color = stage.type === 'published' ? '#10B981' : '#3B82F6';
  return (
    <div className="flex flex-col gap-2 min-w-[110px]">
      <div className="text-[10px] font-mono uppercase tracking-wider text-gray-700">{stage.name}</div>
      <div className={`rounded-lg border-2 ${tone} p-2.5`}>
        <ColumnDots items={stage.items} cols={4} progress={100} color={color} />
      </div>
    </div>
  );
}

export function PipelineRow({ row }: { row: PipelineSiteRow }) {
  const STAGE_ACTIVE = '#3B82F6'; // blue
  const STAGE_HOLD = '#F59E0B'; // amber
  return (
    <div className="rounded-2xl border bg-white shadow-sm p-5 md:p-6">
      <div className="flex flex-wrap items-end gap-6">
        <div className="min-w-[140px]">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-gray-500">Site</div>
          <h3 className="text-2xl font-bold tracking-tight">{row.site}</h3>
          <div className="mt-1 flex items-center gap-2">
            <span className="inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700">
              {row.mode}
            </span>
            <span className="font-mono text-sm text-gray-500">{row.count.toLocaleString()}</span>
          </div>
        </div>
        <div className="flex-1 overflow-x-auto">
          <div className="flex items-end gap-1 min-w-max pb-2">
            {row.stages.map((stage, idx) => (
              <div key={idx} className="flex items-end gap-1">
                {stage.type === 'wait' ? (
                  <WaitStage stage={stage} />
                ) : stage.type === 'done' || stage.type === 'published' ? (
                  <TerminalStage stage={stage} />
                ) : (
                  <div className="flex flex-col gap-2">
                    <div className="text-[10px] font-mono uppercase tracking-wider font-semibold text-gray-700">
                      <span
                        className="inline-flex items-center rounded px-1.5 py-0.5"
                        style={{
                          background: stage.type === 'hold' ? `${STAGE_HOLD}22` : `${STAGE_ACTIVE}22`,
                          color: stage.type === 'hold' ? STAGE_HOLD : STAGE_ACTIVE,
                        }}
                      >
                        {stage.name}
                      </span>
                    </div>
                    <ProcessStage stage={stage} color={stage.type === 'hold' ? STAGE_HOLD : STAGE_ACTIVE} />
                  </div>
                )}
                {idx < row.stages.length - 1 && (
                  <ChevronRight className="h-4 w-4 text-gray-300 mb-6 shrink-0" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
